import ResourceSignedCookieApi from "@apis/resource.signed.cookie.api";
import { cookieManager } from "@utils/cookie.manager";
import { appLocalStorage } from "@utils/app.storage";

export const RESOURCE_TOKEN_LAST_FETCH_STORAGE_KEY = "resourceCookieLastFetch";
const MAX_RETRIES = 1;
const REUSE_WINDOW_MS = 45 * 60 * 1000; // 45 minutes

let inFlightFetch: Promise<void> | null = null;

const requestWithRetry = (token: string, retriesLeft: number): Promise<void> =>
  ResourceSignedCookieApi.requestSignedCookie(token).catch((error: unknown) => {
    if (retriesLeft <= 0) {
      throw error;
    }
    return requestWithRetry(token, retriesLeft - 1);
  });

const getLastFetchAt = (): number | null => {
  const stored = appLocalStorage.getItem(RESOURCE_TOKEN_LAST_FETCH_STORAGE_KEY);
  const lastFetchAt = stored ? Number(JSON.parse(stored)) : NaN;
  return Number.isFinite(lastFetchAt) ? lastFetchAt : null;
};

const isWithinReuseWindow = (): boolean => {
  const lastFetchAt = getLastFetchAt();
  if (lastFetchAt === null) {
    return false;
  }
  const elapsed = Date.now() - lastFetchAt;
  // negative elapsed means the clock moved back, so don't trust the stored value
  return elapsed >= 0 && elapsed < REUSE_WINDOW_MS;
};

export const resourceSignedCookieService = {
  /**
   * Requests a resource signed cookie using the current jwt token.
   * Skipped when one was fetched within the last 45 minutes, or when there is no token.
   * Retries once on failure.
   */
  getResourceSignedCookie(): Promise<void> {
    if (inFlightFetch) {
      return inFlightFetch;
    }

    const token = cookieManager.getCookie("jwt_token");
    if (!token || isWithinReuseWindow()) {
      return Promise.resolve();
    }

    inFlightFetch = requestWithRetry(token, MAX_RETRIES)
      .then(() => {
        appLocalStorage.setItem(RESOURCE_TOKEN_LAST_FETCH_STORAGE_KEY, Date.now());
      })
      .finally(() => {
        inFlightFetch = null;
      });
    return inFlightFetch;
  },

  /** Forget the last fetch time, e.g. on logout, so the next session fetches a fresh cookie. */
  clearLastFetch(): void {
    appLocalStorage.removeItem(RESOURCE_TOKEN_LAST_FETCH_STORAGE_KEY);
  },

  /** Deletes the resource signed cookie; resolves without a request when there is no token. */
  deleteResourceSignedCookie(): Promise<void> {
    const token = cookieManager.getCookie("jwt_token");
    if (!token) {
      return Promise.resolve();
    }
    return ResourceSignedCookieApi.deleteSignedCookie(token);
  },
};
